import React, { useState } from 'react';
import type { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Form, {
	Field,
	FormFooter,
	CharacterCounterField,
	ErrorMessage,
	MessageWrapper,
} from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import { CloseButton, ModalBody, ModalFooter, useModal } from '@atlaskit/modal-dialog';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import TextArea from '@atlaskit/textarea';

import { ADFRenderer } from '@confluence/adf-renderer/entry-points/ADFRenderer.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { SNIPPETS_ONBOARDING } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	ANALYTICS_SOURCE,
	ANALYTICS_ORIGIN,
	ANALYTICS_SUBJECT,
	ANALYTICS_SUBJECT_ID,
} from '../constants/snippetAnalytics.ts';
import { useSnippetsActions } from '../store/snippetsStore.ts';

import { messages } from './CreateSnippetModal.i18n.ts';
import { FirstSnippetCreatedModal } from './FirstSnippetCreatedModal.tsx';

type CreateSnippetModalHeaderProps = {
	onClose: () => void;
};

type CreateSnippetModalDialogProps = {
	onCloseComplete: () => void;
} & CreateSnippetModalHeaderProps &
	WithFlagsProps;

export type CreateSnippetModalProps = {
	adf: any;
	editorAPI?: PublicPluginAPI<[QuickInsertPlugin]> | null;
} & CreateSnippetModalDialogProps;

export enum CreateSnippetModalEvents {
	CANCELLED = 'createSnippetModalCancelled',
	CLOSED = 'createSnippetModalClosed',
	CREATED = 'createSnippetModalCreated',
}

interface CreateSnippetModalForm {
	snippetName: string;
	snippetDescription: string;
}

export const MAX_SNIPPET_NAME_LENGTH = 60;
export const MAX_SNIPPET_DESCRIPTION_LENGTH = 120;

const styles = cssMap({
	formContainer: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
	},
	headerContainer: {
		padding: token('space.300'),
	},
	bodyContainer: {
		height: '100%',
	},
	contentContainer: {
		padding: token('space.300'),
		overflow: 'auto',
		maxHeight: '300px',
		border: `${token('border.width')} solid ${token('color.border.input')}`,
		borderRadius: token('radius.large'),
		scrollbarColor: `${token('color.border.input')} transparent`,
	},
});

const CreateSnippetModalHeader = ({ onClose }: CreateSnippetModalHeaderProps) => {
	const { titleId } = useModal();

	return (
		<Inline
			alignInline="center"
			spread="space-between"
			alignBlock="start"
			xcss={styles.headerContainer}
		>
			<Stack space="space.050">
				<Heading size="medium" id={titleId}>
					<FormattedMessage {...messages.createTemplateModalTitle} />
				</Heading>
				<Text size="medium" color="color.text.subtle">
					<FormattedMessage {...messages.createTemplateModalDescription} />
				</Text>
			</Stack>
			<CloseButton onClick={onClose} />
		</Inline>
	);
};

const CreateSnippetModalWithFlags: FC<CreateSnippetModalProps> = ({
	adf,
	onClose,
	flags,
	editorAPI,
}) => {
	const [adfError, setADFError] = useState<any>(null);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { userId, cloudId } = useSessionData();
	const { Modal } = useChoreographerAssets();
	const { showModal } = useDialogs();
	const intl = useIntl();
	const snippetsActions = useSnippetsActions();

	const {
		data: onboardingData,
		loading: onboardingLoading,
		error: onboardingError,
	} = useGetOnboardingState([SNIPPETS_ONBOARDING.HAS_SEEN_FIRST_SNIPPET_CREATED_MODAL]);
	const { hasSeenFirstSnippetCreatedModal } = deserializeState(onboardingData);

	const fireCreateSnippetModalAnalytics = (event: CreateSnippetModalEvents, snippetId?: string) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: ANALYTICS_SOURCE,
				action: 'closed',
				actionSubject: ANALYTICS_SUBJECT.BUTTON,
				actionSubjectId: ANALYTICS_SUBJECT_ID.CREATE_SNIPPET,
				type: 'ui',
				attributes: {
					origin: ANALYTICS_ORIGIN.CREATE_SNIPPET_MODAL,
					event,
					...(userId != null && { userId }),
					...(cloudId != null && { cloudId }),
					// TODO: When private/shared (site-wide) field is added to this modal, pass the user's choice here ('private' or 'shared') for analytics attributes.scope. Note: use 'shared' for site-wide, not 'global'—global in the API means Atlassian-owned.
					scope: 'private',
					...(snippetId != null && { snippetId }),
				},
			},
		}).fire();
	};

	const exitModal = (event: CreateSnippetModalEvents) => {
		fireCreateSnippetModalAnalytics(event);
		onClose();
	};

	const submit = async (values: CreateSnippetModalForm): Promise<void> => {
		// The action handles flags, monitoring, and adding to quick insert menu internally
		const newSnippet = await snippetsActions.createSnippet(
			values.snippetName,
			adf,
			values.snippetDescription,
			{
				flags,
				intl,
				editorAPI: editorAPI ?? undefined,
			},
		);

		// TODO(analytics): When AI name/description is added, set wasNameSuggestedByAI and nameKeptAsSuggested (and description equivalents) on CREATED event to measure "how often we get the name right on creation".
		fireCreateSnippetModalAnalytics(CreateSnippetModalEvents.CREATED, newSnippet?.snippetId);
		onClose();

		const isOnboardingStateReady = !onboardingLoading && !onboardingError;
		const shouldShowFirstSnippetCreatedModal =
			isOnboardingStateReady && !hasSeenFirstSnippetCreatedModal;
		if (shouldShowFirstSnippetCreatedModal) {
			showModal(FirstSnippetCreatedModal, {});
		}
	};

	const handleADFError = (error: any) => {
		setADFError(error);
	};

	return (
		<Modal height="750px">
			<CreateSnippetModalHeader onClose={() => exitModal(CreateSnippetModalEvents.CLOSED)} />
			<Form<CreateSnippetModalForm> noValidate onSubmit={submit}>
				{({ submitting, formProps }) => (
					<form {...formProps} css={styles.formContainer}>
						<ModalBody>
							<Stack space="space.300" xcss={styles.bodyContainer}>
								<Field
									name="snippetName"
									label={<FormattedMessage {...messages.createTemplateModalNameLabel} />}
									defaultValue=""
									helperMessage={<FormattedMessage {...messages.createTemplateModalNameHelper} />}
									component={({ fieldProps }) => (
										<Textfield
											{...fieldProps}
											maxLength={MAX_SNIPPET_NAME_LENGTH}
											placeholder={intl.formatMessage(messages.createTemplateModalNamePlaceholder)}
										/>
									)}
									validate={(value) =>
										value ? undefined : intl.formatMessage(messages.createTemplateModalNameError)
									}
									isRequired
								/>
								<CharacterCounterField<string, HTMLTextAreaElement>
									name="snippetDescription"
									label={<FormattedMessage {...messages.createTemplateModalDescriptionLabel} />}
									maxCharacters={MAX_SNIPPET_DESCRIPTION_LENGTH}
								>
									{({ fieldProps }) => (
										<TextArea
											{...fieldProps}
											placeholder={intl.formatMessage(
												messages.createTemplateModalDescriptionPlaceholder,
											)}
										/>
									)}
								</CharacterCounterField>
								<Stack grow="fill">
									{/* This <Stack> is here to make sure it has full height and the ADF renderer can be displayed properly */}
									<Stack grow="fill" xcss={styles.contentContainer}>
										<ADFRenderer
											document={adf}
											appearance="comment"
											allowPlaceholderText
											onError={handleADFError}
										/>
									</Stack>
									<Field
										name="snippetBody"
										defaultValue={adfError} // Must be here to make sure validation works properly.
										validate={() =>
											adfError
												? intl.formatMessage(messages.createTemplateModalAdfError)
												: undefined
										}
									>
										{({ error }) => (
											// This <Field> is here to show the error message when the ADF is invalid
											// and for <Form> validation to work comprehensively for name, description,
											// and ADF and prevent `Create` from being triggered for all cases.
											<MessageWrapper>
												{error && <ErrorMessage>{error}</ErrorMessage>}
											</MessageWrapper>
										)}
									</Field>
								</Stack>
							</Stack>
						</ModalBody>

						<ModalFooter>
							<FormFooter>
								<ButtonGroup>
									<Button
										appearance="subtle"
										onClick={() => exitModal(CreateSnippetModalEvents.CANCELLED)}
									>
										<FormattedMessage {...messages.createTemplateModalCancelButton} />
									</Button>
									<Button type="submit" appearance="primary" isLoading={submitting}>
										<FormattedMessage {...messages.createTemplateModalCreateButton} />
									</Button>
								</ButtonGroup>
							</FormFooter>
						</ModalFooter>
					</form>
				)}
			</Form>
		</Modal>
	);
};

export const CreateSnippetModal = withFlags(CreateSnippetModalWithFlags);
